import { AppLogo, Button, Spinner } from '@1k5-tech-es/grado-react';
import { ThemeToggle } from '@1k5-tech-es/grado-react/labs';
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

import { consumeReturnTo, redirectToLogin, refreshSession, sendRaw } from '../api/client';
import type { SessionInfo } from '../api/types';
import { readLoginReturn, type LoginReturn } from './loginReturn';

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; session: SessionInfo }
  | { status: 'signed-out' }
  | { status: 'error'; message: string };

interface AuthContextValue {
  session: SessionInfo;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const REFRESH_MARGIN_MS = 60_000;

/**
 * Session lifecycle against IDHub (through our backend):
 *   return from IDHub  tokens in the URL fragment (on /auth/callback, or on the
 *                      app's URL when chosen from IDHub's application list):
 *                      clean the URL at once, hand them to the backend
 *                      (httpOnly cookies), continue.
 *   any other page  ask the backend who we are; refresh once if expired;
 *                   otherwise go to the IDHub login page.
 * The access token is renewed ~1 minute before it expires.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return; // StrictMode runs effects twice in development
    started.current = true;
    void bootstrap().then(setState);
  }, []);

  // Proactive refresh before the 15-minute access token expires
  useEffect(() => {
    if (state.status !== 'authenticated') return;
    const delay = Math.max(5_000, new Date(state.session.expiresAt).getTime() - Date.now() - REFRESH_MARGIN_MS);
    const timer = window.setTimeout(async () => {
      if (await refreshSession()) {
        const me = await loadSession();
        if (me) return setState({ status: 'authenticated', session: me });
      }
      await redirectToLogin();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [state]);

  /** For a user waiting for approval: ask again without signing in again. */
  const recheck = useCallback(async () => {
    const me = await loadSession();
    setState(me ? { status: 'authenticated', session: me } : await bootstrap());
  }, []);

  const logout = useCallback(async () => {
    await sendRaw('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setState({ status: 'signed-out' });
  }, []);

  if (state.status === 'loading') return <FullScreenMessage title="Conectando con IDHub…" spinner />;
  if (state.status === 'signed-out') {
    return (
      <FullScreenMessage title="Has cerrado sesión" text="Tu sesión en esta aplicación se ha cerrado correctamente.">
        <Button size="lg" className="mt-6" onClick={() => void redirectToLogin()}>
          Volver a entrar con IDHub
        </Button>
      </FullScreenMessage>
    );
  }
  if (state.status === 'error') {
    return (
      <FullScreenMessage title="No se ha podido iniciar sesión" text={state.message}>
        <Button size="lg" className="mt-6" onClick={() => void redirectToLogin()}>
          Reintentar
        </Button>
      </FullScreenMessage>
    );
  }
  const { user } = state.session;
  if (user.access !== 'approved') {
    const pending = user.access === 'pending';
    return (
      <FullScreenMessage
        title={pending ? 'Tu acceso está pendiente de aprobación' : 'No tienes acceso a esta aplicación'}
        text={
          pending
            ? `Has iniciado sesión como ${user.email}. Por la sensibilidad de los datos, un administrador de Performance tiene que aprobar tu acceso antes de que puedas verlos.`
            : `Has iniciado sesión como ${user.email}, pero un administrador de Performance ha denegado o retirado tu acceso. Si crees que es un error, contacta con él.`
        }
      >
        <div className="mt-6 flex gap-3">
          {pending && (
            <Button onClick={() => void recheck()}>Comprobar de nuevo</Button>
          )}
          <Button variant="secondary" onClick={() => void logout()}>
            Cerrar sesión
          </Button>
        </div>
      </FullScreenMessage>
    );
  }
  return <AuthContext.Provider value={{ session: state.session, logout }}>{children}</AuthContext.Provider>;
}

async function bootstrap(): Promise<AuthState> {
  const loginReturn = readLoginReturn(window.location.pathname, window.location.hash);
  if (loginReturn) return completeLogin(loginReturn);
  const session = await loadSession();
  if (session) return { status: 'authenticated', session };
  if (await refreshSession()) {
    const refreshed = await loadSession();
    if (refreshed) return { status: 'authenticated', session: refreshed };
  }
  return redirectToLogin();
}

async function completeLogin({ accessToken, refreshToken }: LoginReturn): Promise<AuthState> {
  // Remove the tokens from the address bar and history immediately.
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  if (!accessToken || !refreshToken) return redirectToLogin();

  const response = await sendRaw('/api/auth/session', {
    method: 'POST',
    body: JSON.stringify({ accessToken, refreshToken }),
  });
  if (!response.ok) {
    return {
      status: 'error',
      message:
        response.status === 401
          ? 'IDHub ha devuelto un token que esta aplicación no acepta. Comprueba que tienes acceso a la aplicación.'
          : 'Error inesperado al crear la sesión.',
    };
  }
  window.history.replaceState(null, '', consumeReturnTo());
  return { status: 'authenticated', session: (await response.json()) as SessionInfo };
}

async function loadSession(): Promise<SessionInfo | null> {
  const response = await sendRaw('/api/auth/me');
  return response.ok ? ((await response.json()) as SessionInfo) : null;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}

function FullScreenMessage({
  title,
  text,
  spinner,
  children,
}: {
  title: string;
  text?: string;
  spinner?: boolean;
  children?: ReactNode;
}) {
  return (
    // Seen by people without access yet: brand theme, like other public pages.
    <main
      data-k5-theme="marca"
      className="relative flex min-h-dvh flex-col items-center justify-center bg-background px-6 text-center text-foreground"
    >
      <div className="absolute top-4 right-4">
        <ThemeToggle defaultMode="light" />
      </div>
      <AppLogo className="mb-8 h-12 w-auto text-primary" />
      {spinner && <Spinner size="lg" className="mb-4" />}
      <h1 className="text-lg font-semibold">{title}</h1>
      {text && <p className="mt-2 max-w-md text-sm text-muted-foreground">{text}</p>}
      {children}
    </main>
  );
}
